import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import JSZip from 'jszip';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiFile, FiLoader } = FiIcons;

function FileUploader({ onFilesExtracted }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const supportedExtensions = [
    '.js', '.jsx', '.ts', '.tsx', '.py', '.java', '.cpp', '.c', '.cs',
    '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.dart', '.vue',
    '.html', '.css', '.scss', '.sass', '.less', '.xml', '.json',
    '.yml', '.yaml', '.md', '.txt', '.sql', '.sh', '.bat', '.ps1'
  ];

  const isTextFile = (filename) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return supportedExtensions.includes(ext);
  };

  const processZipFile = async (file) => {
    setIsProcessing(true);
    setError('');
    
    try {
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(file);
      const extractedFiles = [];

      for (const [path, zipEntry] of Object.entries(zipContent.files)) {
        if (!zipEntry.dir && isTextFile(path)) {
          try {
            const content = await zipEntry.async('text');
            extractedFiles.push({
              path,
              name: path.split('/').pop(),
              content,
              size: content.length,
              type: path.substring(path.lastIndexOf('.') + 1).toUpperCase()
            });
          } catch (err) {
            console.warn(`Could not read file ${path}:`, err);
          }
        }
      }

      if (extractedFiles.length === 0) {
        setError('No supported code files found in the zip archive.');
        return;
      }

      onFilesExtracted(extractedFiles);
    } catch (err) {
      setError('Failed to process zip file. Please ensure it\'s a valid zip archive.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const zipFile = files.find(file => file.type === 'application/zip' || file.name.endsWith('.zip'));
    
    if (zipFile) {
      processZipFile(zipFile);
    } else {
      setError('Please upload a valid ZIP file.');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processZipFile(file);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        className={`
          relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300
          ${isDragging 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-slate-300 bg-white hover:border-blue-300 hover:bg-slate-50'
          }
          ${isProcessing ? 'pointer-events-none opacity-60' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div className="space-y-4">
          {isProcessing ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <SafeIcon icon={FiLoader} className="text-4xl text-blue-600 mx-auto" />
            </motion.div>
          ) : (
            <SafeIcon icon={FiUpload} className="text-4xl text-slate-400 mx-auto" />
          )}
          
          <div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              {isProcessing ? 'Processing ZIP file...' : 'Upload ZIP File'}
            </h3>
            <p className="text-slate-600 mb-4">
              Drop your ZIP file here or click to browse
            </p>
            
            <motion.button
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isProcessing}
            >
              <SafeIcon icon={FiFile} className="inline mr-2" />
              Choose File
            </motion.button>
          </div>
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700"
        >
          {error}
        </motion.div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Supported File Types:</h4>
        <p className="text-sm text-blue-700">
          JavaScript, TypeScript, Python, Java, C/C++, C#, PHP, Ruby, Go, Rust, Swift, 
          Kotlin, Dart, Vue, HTML, CSS, SCSS, XML, JSON, YAML, Markdown, SQL, Shell scripts
        </p>
      </div>
    </div>
  );
}

export default FileUploader;